import * as yup from 'yup';

import { notReachable } from 'utils/types/not-reachable';

import {
  FIELD_NAMES,
  MAX_SAVINGS_THRESHOLD_PERCENT,
  MIN_CYCLE_INTERVAL_SECONDS,
  MIN_NODE_AGE_SECONDS,
  MIN_SAVINGS_THRESHOLD_PERCENT,
} from './_constants';
import {
  MAX_DRAIN_TIMEOUT_MINUTES,
  MIN_DRAIN_TIMEOUT_MINUTES,
} from 'features/cluster/rebalance/constants';
import { FormValues } from './_types';
import { parseEvictionConfig } from './_utils';

const cycleIntervalSecondsValidation = yup
  .number()
  .min(
    MIN_CYCLE_INTERVAL_SECONDS,
    `Must be greater than or equal to ${MIN_CYCLE_INTERVAL_SECONDS} seconds`
  );

export const validationSchema = yup.object<FormValues>({
  [FIELD_NAMES.enabled]: yup.boolean().required(),
  [FIELD_NAMES.mode]: yup.string().required(),
  [FIELD_NAMES.fullModeCycleIntervalSeconds]: cycleIntervalSecondsValidation,
  [FIELD_NAMES.drainOnlyCycleIntervalSeconds]: cycleIntervalSecondsValidation,
  [FIELD_NAMES.deleteEmptyCycleIntervalSeconds]: cycleIntervalSecondsValidation,
  [FIELD_NAMES.minNodeAgeSeconds]: yup
    .number()
    .min(
      MIN_NODE_AGE_SECONDS,
      `Must be greater than or equal to ${MIN_NODE_AGE_SECONDS} seconds`
    ),
  [FIELD_NAMES.savingsThresholdEnabled]: yup.boolean(),
  [FIELD_NAMES.savingsThresholdPercent]: yup
    .number()
    .min(
      MIN_SAVINGS_THRESHOLD_PERCENT,
      `Must be greater than or equal to ${MIN_SAVINGS_THRESHOLD_PERCENT}%`
    )
    .max(
      MAX_SAVINGS_THRESHOLD_PERCENT,
      `Must be less than or equal to ${MAX_SAVINGS_THRESHOLD_PERCENT}%`
    )
    .nullable(),
  [FIELD_NAMES.aggressiveModeConfig]: yup.object({
    [FIELD_NAMES.aggressiveModeConfigDrainTimeoutMinutes]: yup
      .number()
      .when(FIELD_NAMES.aggressiveModeConfigAggressiveEviction, {
        is: true,
        then: (schema) =>
          schema
            .required('Field is required')
            .min(
              MIN_DRAIN_TIMEOUT_MINUTES,
              `Must be greater than or equal to ${MIN_DRAIN_TIMEOUT_MINUTES}`
            )
            .max(
              MAX_DRAIN_TIMEOUT_MINUTES,
              `Must be less than or equal to ${MAX_DRAIN_TIMEOUT_MINUTES}`
            ),
        otherwise: (schema) => schema.nullable(),
      }),
  }),
  [FIELD_NAMES.advancedConfiguration]: yup.string().test({
    name: 'valid-evictor-config',
    test(value, ctx) {
      if (!value) {
        return true;
      }

      const result = parseEvictionConfig(value);
      switch (result.type) {
        case 'success':
          return true;
        case 'error': {
          const { error } = result;
          if (error instanceof yup.ValidationError) {
            return ctx.createError({ message: error.errors[0] });
          }
          if (error instanceof Error) {
            return ctx.createError({ message: error.message });
          }
          return ctx.createError({ message: 'Invalid configuration' });
        }
        default:
          return notReachable(result);
      }
    },
  }),
});
